import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

import type {
  DiaComparecimentoStatus,
  RelatorioCivelFormData,
} from '../pages/relatorio-civel-page.models';

import { MESES } from '@/shared/utils/datas';
import { escapeHtml } from '@/shared/utils/impressao';

/** Linha do quadro de resumo (nivel 1 = subseção). */
export interface ResumoRelatorioLinha {
  label: string;
  /** `null` para seções que só agregam subseções. */
  quantidade: number | null;
  nivel: 0 | 1;
}

/** Seção com itens do relatório, pronta para virar aba de planilha ou tabela impressa. */
export interface SecaoRelatorioExport {
  /** Nome da aba na planilha (limite de 31 caracteres do Excel). */
  aba: string;
  titulo: string;
  colunas: string[];
  linhas: string[][];
}

/** Dados de identificação exibidos no topo da impressão e da planilha. */
export interface RelatorioExportMeta {
  defensor: string;
  titularidade: string;
  periodo: string;
  defensoria: string;
  tipo: string;
}

/** Dia do calendário de comparecimento já resolvido em texto. */
export interface DiaCalendarioImpressao {
  dia: number;
  isMesAtual: boolean;
  /** Situação marcada no dia (ex.: `Presente`, `Ausente`, `Licença Outros`). */
  situacao: string;
  /** Evento do dia que dispensa comparecimento (ex.: `Feriado`, `Férias`). */
  evento: string;
}

export interface SemanaCalendarioImpressao {
  nome: string;
  dias: (DiaCalendarioImpressao | null)[];
}

/** Calendário de comparecimento no mesmo formato da tela, sem cores. */
export interface CalendarioComparecimentoImpressao {
  diasPresenca: number;
  semanas: SemanaCalendarioImpressao[];
}

const LABEL_STATUS_DIA: Record<DiaComparecimentoStatus['status'], string> = {
  presente: 'Presente',
  ausente: 'Ausente',
  remoto: 'Remoto',
  impedimento: 'Impedimento',
  nao_marcado: 'Não marcado',
};

/** Período do relatório no formato "Agosto/2026". */
export function periodoRelatorioLabel(mes: string | number, ano: number): string {
  return `${MESES[Number(mes) - 1] ?? String(mes)}/${ano}`;
}

function formatarDataBr(valor?: Date | string | null): string {
  if (!valor) return '';
  const data = valor instanceof Date ? valor : new Date(valor);
  return Number.isNaN(data.getTime()) ? '' : data.toLocaleDateString('pt-BR');
}

/** Situação do dia em texto; dias sem marcação ficam em branco na impressão. */
export function rotuloSituacaoDiaImpressao(status?: DiaComparecimentoStatus['status']): string {
  if (!status || status === 'nao_marcado') return '';
  return LABEL_STATUS_DIA[status];
}

function situacaoDia(dia: DiaComparecimentoStatus): string {
  if (dia.status === 'impedimento') {
    return dia.impedimentoDescricao?.trim() || LABEL_STATUS_DIA.impedimento;
  }
  return LABEL_STATUS_DIA[dia.status];
}

function observacaoDia(dia: DiaComparecimentoStatus): string {
  const observacoes: string[] = [];
  if (dia.status === 'remoto' && dia.remotoValidado) observacoes.push('Remoto validado pela Corregedoria');
  if (dia.status === 'ausente' && dia.ausenteAceitoCorregedoria) observacoes.push('Ausência aceita pela Corregedoria');
  if (dia.justificativaArquivoNome?.trim() || dia.justificativaArquivo) observacoes.push('Com anexo');
  return observacoes.join('; ');
}

/** Dias com justificativa ou decisão registrada, detalhados abaixo do calendário. */
function linhasJustificativasComparecimento(formData: RelatorioCivelFormData): string[][] {
  const mesRelatorio = String(formData.mes).padStart(2, '0');
  const anoRelatorio = Number(formData.ano);

  return formData.comparecimento.diasStatus
    .filter((dia) => {
      const mes = dia.mes === undefined ? mesRelatorio : String(dia.mes).padStart(2, '0');
      const ano = dia.ano === undefined ? anoRelatorio : Number(dia.ano);
      if (mes !== mesRelatorio || ano !== anoRelatorio) return false;
      return Boolean(dia.justificativaTexto?.trim()) || Boolean(observacaoDia(dia));
    })
    .slice()
    .sort((a, b) => a.dia - b.dia)
    .map((dia) => [
      `${String(dia.dia).padStart(2, '0')}/${mesRelatorio}/${anoRelatorio}`,
      situacaoDia(dia),
      dia.justificativaTexto?.trim() || '',
      observacaoDia(dia),
    ]);
}

const COLUNAS_CALENDARIO = ['Semana', 'Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

/** Texto exibido na célula do dia: situação e/ou evento, sem repetir o mesmo rótulo. */
function textoDiaCalendario(dia: DiaCalendarioImpressao): string {
  if (!dia.isMesAtual) return '';
  const rotulos = [dia.situacao, dia.evento].filter(Boolean);
  return [...new Set(rotulos)].join(' · ');
}

function linhasCalendarioComparecimento(calendario: CalendarioComparecimentoImpressao): string[][] {
  return calendario.semanas.map((semana) => [
    semana.nome,
    ...semana.dias.map((dia) => {
      if (!dia) return '';
      const texto = textoDiaCalendario(dia);
      return texto ? `${dia.dia} ${texto}` : String(dia.dia);
    }),
  ]);
}

function linhasProcessos(processos: { numeroProcesso: string; dataAdicao: Date }[]): string[][] {
  return processos.map((processo) => [processo.numeroProcesso, formatarDataBr(processo.dataAdicao)]);
}

const COLUNAS_PROCESSO = ['Nº do processo', 'Adicionado em'];

/**
 * Seções do relatório com seus itens, na ordem das etapas do wizard.
 * Seções sem itens são mantidas para que o consumidor decida se exibe ou não.
 */
export function montarSecoesRelatorioCivel(formData: RelatorioCivelFormData): SecaoRelatorioExport[] {
  return [
    {
      aba: '01 Comparec. Justificativas',
      titulo: '1. Comparecimento — justificativas e decisões',
      colunas: ['Data', 'Situação', 'Justificativa', 'Observação'],
      linhas: linhasJustificativasComparecimento(formData),
    },
    {
      aba: '02 Autor e Natureza',
      titulo: '2. Autor e Natureza da Ação',
      colunas: ['Autor', 'Natureza da ação', 'Adicionado em'],
      linhas: formData.autorNatureza.itens.map((item) => [
        item.autor,
        item.naturezaAcao,
        formatarDataBr(item.dataAdicao),
      ]),
    },
    {
      aba: '03 Contestacao',
      titulo: '3. Contestação',
      colunas: COLUNAS_PROCESSO,
      linhas: linhasProcessos(formData.contestacao.processos),
    },
    {
      aba: '04 Reconvencao',
      titulo: '4. Reconvenção',
      colunas: COLUNAS_PROCESSO,
      linhas: linhasProcessos(formData.reconvencao.processos),
    },
    {
      aba: '05 Replica',
      titulo: '5. Réplica',
      colunas: COLUNAS_PROCESSO,
      linhas: linhasProcessos(formData.replica.processos),
    },
    {
      aba: '06 Manifestacao',
      titulo: '6. Manifestação nos Autos',
      colunas: COLUNAS_PROCESSO,
      linhas: linhasProcessos(formData.manifestacao.processos),
    },
    {
      aba: '07 Razoes Finais',
      titulo: '7. Razões Finais',
      colunas: COLUNAS_PROCESSO,
      linhas: linhasProcessos(formData.razoesFinais.processos),
    },
    {
      aba: '08 Aud. Presenciais',
      titulo: '8. Audiências/Sessões Presenciais',
      colunas: ['Tipo de audiência', 'Nº / protocolo', 'Data'],
      linhas: formData.audiencias.presenciais.map((item) => [
        item.audienciaPresencial,
        item.numeroProtocolo,
        formatarDataBr(item.data),
      ]),
    },
    {
      aba: '08 Aud. Remotas',
      titulo: '8. Audiências/Sessões Remotas',
      colunas: ['Tipo de audiência', 'Nº do processo', 'Data'],
      linhas: formData.audiencias.remotas.map((item) => [
        item.audienciaRemota,
        item.numeroProcesso,
        formatarDataBr(item.data),
      ]),
    },
    {
      aba: '09 Sust. Oral',
      titulo: '9. Sustentação Oral',
      colunas: ['Nº do processo', 'Parte', 'Adicionado em'],
      linhas: formData.sustentacaoOral.itens.map((item) => [
        item.numeroProcesso,
        item.parte,
        formatarDataBr(item.dataAdicao),
      ]),
    },
    {
      aba: '10 Recursos',
      titulo: '10. Recursos',
      colunas: ['Nº do processo', 'Tipo Recurso', 'Adicionado em'],
      linhas: formData.recursos.processos.map((item) => [
        item.numeroProcesso,
        item.tipoRecurso || '',
        formatarDataBr(item.dataAdicao),
      ]),
    },
    {
      aba: '11 Atend. Presenciais',
      titulo: '11. Atendimentos Presenciais',
      colunas: ['Arquivo', 'Total', 'Observação'],
      linhas: formData.atendimento.presenciais.length
        ? formData.atendimento.presenciais.map((item, index) => [
            item.arquivoNome || item.arquivo?.name || '',
            index === 0 ? String(formData.atendimento.totalPresenciais ?? 0) : '',
            item.observacao,
          ])
        : [['', String(formData.atendimento.totalPresenciais ?? 0), '']],
    },
    {
      aba: '11 Atend. Remotos',
      titulo: '11. Atendimentos Remotos',
      colunas: ['Arquivo', 'Total', 'Observação'],
      linhas: formData.atendimento.remotas.length
        ? formData.atendimento.remotas.map((item, index) => [
            item.arquivoNome || item.arquivo?.name || '',
            index === 0 ? String(formData.atendimento.totalRemotas ?? 0) : '',
            item.observacao,
          ])
        : [['', String(formData.atendimento.totalRemotas ?? 0), '']],
    },
    {
      aba: '12 Acordos',
      titulo: '12. Acordos Extrajudiciais',
      colunas: ['Nome da parte', 'Anexo', 'Adicionado em'],
      linhas: formData.acordosExtrajudiciais.itens.map((item) => [
        item.nomeParte,
        item.anexoNome || item.anexo?.name || '',
        formatarDataBr(item.dataAdicao),
      ]),
    },
    {
      aba: '13 Hon. Sentenca',
      titulo: '13. Honorários — Sentença/Acórdão',
      colunas: COLUNAS_PROCESSO,
      linhas: linhasProcessos(formData.honorarios.sentencaAcordao),
    },
    {
      aba: '13 Hon. Cumprimento',
      titulo: '13. Honorários — Cumprimento',
      colunas: COLUNAS_PROCESSO,
      linhas: linhasProcessos(formData.honorarios.cumprimento),
    },
    {
      aba: '14 Of. Requisitorios',
      titulo: '14. Ofícios Requisitórios',
      colunas: COLUNAS_PROCESSO,
      linhas: linhasProcessos(formData.oficiosExpedidos.requisitorios),
    },
    {
      aba: '14 Of. Demais',
      titulo: '14. Demais Ofícios',
      colunas: COLUNAS_PROCESSO,
      linhas: linhasProcessos(formData.oficiosExpedidos.demais),
    },
    {
      aba: '15 Desig. Processos',
      titulo: '15. Designação Especial (Processos)',
      colunas: ['Tipo de peça', 'Nº do processo', 'Defensoria/Vara', 'Adicionado em'],
      linhas: formData.designacaoEspecialProcessos.itens.map((item) => [
        item.tipoPeca,
        item.numeroProcesso,
        item.defensoriaLabel || '',
        formatarDataBr(item.dataAdicao),
      ]),
    },
    {
      aba: '16 Desig. Audiencias',
      titulo: '16. Designação Especial (Audiências)',
      colunas: ['Audiência', 'Nº do processo', 'Data', 'Defensoria/Vara'],
      linhas: formData.designacaoEspecialAudiencias.itens.map((item) => [
        item.audiencia,
        item.numeroProcesso,
        formatarDataBr(item.data),
        item.defensoriaLabel || '',
      ]),
    },
    {
      aba: '17 Outros',
      titulo: '17. Outros Procedimentos',
      colunas: COLUNAS_PROCESSO,
      linhas: linhasProcessos(formData.outrosProcedimentos.processos),
    },
  ];
}

/** Quadro de quantidades por seção (mesma base usada no modal de resumo). */
export function montarResumoRelatorioCivel(formData: RelatorioCivelFormData): ResumoRelatorioLinha[] {
  return [
    { label: '2. Autor e Natureza da Ação', quantidade: formData.autorNatureza.itens.length, nivel: 0 },
    { label: '3. Contestação', quantidade: formData.contestacao.processos.length, nivel: 0 },
    { label: '4. Reconvenção', quantidade: formData.reconvencao.processos.length, nivel: 0 },
    { label: '5. Réplica', quantidade: formData.replica.processos.length, nivel: 0 },
    { label: '6. Manifestação nos Autos', quantidade: formData.manifestacao.processos.length, nivel: 0 },
    { label: '7. Razões Finais', quantidade: formData.razoesFinais.processos.length, nivel: 0 },
    { label: '8. Audiências/Sessões', quantidade: null, nivel: 0 },
    { label: 'Audiências Presenciais', quantidade: formData.audiencias.presenciais.length, nivel: 1 },
    { label: 'Audiências Remotas', quantidade: formData.audiencias.remotas.length, nivel: 1 },
    { label: '9. Sustentação Oral', quantidade: formData.sustentacaoOral.itens.length, nivel: 0 },
    { label: '10. Recursos', quantidade: formData.recursos.processos.length, nivel: 0 },
    { label: '11. Atendimentos', quantidade: null, nivel: 0 },
    { label: 'Atendimentos Presenciais', quantidade: formData.atendimento.totalPresenciais ?? 0, nivel: 1 },
    { label: 'Atendimentos Remotos', quantidade: formData.atendimento.totalRemotas ?? 0, nivel: 1 },
    { label: '12. Acordos Extrajudiciais', quantidade: formData.acordosExtrajudiciais.itens.length, nivel: 0 },
    { label: '13. Honorários', quantidade: null, nivel: 0 },
    { label: 'Sentença/Acórdão', quantidade: formData.honorarios.sentencaAcordao.length, nivel: 1 },
    { label: 'Cumprimento', quantidade: formData.honorarios.cumprimento.length, nivel: 1 },
    { label: '14. Ofícios Expedidos', quantidade: null, nivel: 0 },
    { label: 'Requisitórios', quantidade: formData.oficiosExpedidos.requisitorios.length, nivel: 1 },
    { label: 'Demais', quantidade: formData.oficiosExpedidos.demais.length, nivel: 1 },
    {
      label: '15. Designação Especial (Processos)',
      quantidade: formData.designacaoEspecialProcessos.itens.length,
      nivel: 0,
    },
    {
      label: '16. Designação Especial (Audiências)',
      quantidade: formData.designacaoEspecialAudiencias.itens.length,
      nivel: 0,
    },
    { label: '17. Outros Procedimentos', quantidade: formData.outrosProcedimentos.processos.length, nivel: 0 },
  ];
}

/**
 * Total de itens das seções 2 a 17 (não inclui os dias de comparecimento).
 * Seções que só agregam subseções entram com quantidade nula e não são somadas duas vezes.
 */
export function totalItensRelatorioCivel(formData: RelatorioCivelFormData): number {
  return montarResumoRelatorioCivel(formData).reduce((total, linha) => total + (linha.quantidade ?? 0), 0);
}

function camposIdentificacao(meta: RelatorioExportMeta): { rotulo: string; valor: string }[] {
  return [
    { rotulo: 'Defensor', valor: meta.defensor },
    { rotulo: 'Titularidade', valor: meta.titularidade },
    { rotulo: 'Período', valor: meta.periodo },
    { rotulo: 'Defensoria/Vara', valor: meta.defensoria },
    { rotulo: 'Tipo', valor: meta.tipo },
  ];
}

/**
 * Identificação em poucas linhas de texto corrido; na planilha o conteúdo
 * transborda para as células vazias à direita, dispensando uma tabela.
 */
function linhasIdentificacaoCompacta(meta: RelatorioExportMeta): string[] {
  const campos = camposIdentificacao(meta).map((campo) => `${campo.rotulo}: ${campo.valor}`);
  return [campos.slice(0, 3).join('   |   '), campos.slice(3).join('   |   ')];
}

/** Quantidade de pares "seção + quantidade" por linha no quadro de resumo. */
const PARES_RESUMO_POR_LINHA = 3;

function rotuloResumo(linha: ResumoRelatorioLinha): string {
  return linha.nivel === 1 ? `· ${linha.label}` : linha.label;
}

/**
 * Distribui o resumo em várias colunas (pares seção/quantidade) para ocupar
 * melhor a largura da página e da planilha.
 */
function linhasResumoGrid(resumo: ResumoRelatorioLinha[]): (string | number)[][] {
  const linhas: (string | number)[][] = [];

  for (let inicio = 0; inicio < resumo.length; inicio += PARES_RESUMO_POR_LINHA) {
    const linha: (string | number)[] = [];
    for (let coluna = 0; coluna < PARES_RESUMO_POR_LINHA; coluna++) {
      const item = resumo[inicio + coluna];
      linha.push(item ? rotuloResumo(item) : '', item ? (item.quantidade ?? '—') : '');
    }
    linhas.push(linha);
  }

  return linhas;
}

export function nomeArquivoRelatorioCivel(formData: RelatorioCivelFormData): string {
  return `relatorio-atividades-civel-${formData.ano}-${String(formData.mes).padStart(2, '0')}.xlsx`;
}

/** Gera e baixa a planilha com identificação, resumo, calendário e uma aba por seção preenchida. */
export function exportarRelatorioCivelExcel(
  formData: RelatorioCivelFormData,
  meta: RelatorioExportMeta,
  calendario: CalendarioComparecimentoImpressao,
): void {
  const workbook = XLSX.utils.book_new();

  const abaResumo: (string | number)[][] = [
    ['Relatório de Atividades - Cível'],
    ...linhasIdentificacaoCompacta(meta).map((linha) => [linha]),
    [],
    ...linhasResumoGrid(montarResumoRelatorioCivel(formData)),
    [],
    ['Total de itens', totalItensRelatorioCivel(formData)],
  ];
  const planilhaResumo = XLSX.utils.aoa_to_sheet(abaResumo);
  planilhaResumo['!cols'] = Array.from({ length: PARES_RESUMO_POR_LINHA * 2 }, (_, indice) => ({
    wch: indice % 2 === 0 ? 34 : 8,
  }));
  XLSX.utils.book_append_sheet(workbook, planilhaResumo, 'Resumo');

  const abaCalendario: (string | number)[][] = [
    ['1. Comparecimento ao Fórum', meta.periodo],
    ['Dias de presença', calendario.diasPresenca],
    [],
    COLUNAS_CALENDARIO,
    ...linhasCalendarioComparecimento(calendario),
  ];
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(abaCalendario), '01 Comparecimento');

  for (const secao of montarSecoesRelatorioCivel(formData)) {
    if (secao.linhas.length === 0) continue;
    const planilha = XLSX.utils.aoa_to_sheet([secao.colunas, ...secao.linhas]);
    XLSX.utils.book_append_sheet(workbook, planilha, secao.aba.slice(0, 31));
  }

  const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([buffer], { type: 'application/octet-stream' }), nomeArquivoRelatorioCivel(formData));
}

function tabelaHtml(colunas: string[], linhas: string[][]): string {
  const cabecalho = colunas.map((coluna) => `<th>${escapeHtml(coluna)}</th>`).join('');
  const corpo = linhas
    .map((linha) => `<tr>${linha.map((celula) => `<td>${escapeHtml(celula)}</td>`).join('')}</tr>`)
    .join('');

  return `<table><thead><tr>${cabecalho}</tr></thead><tbody>${corpo}</tbody></table>`;
}

function tituloSecaoHtml(titulo: string): string {
  return `<h3 style="text-align:left;font-size:12pt;margin:1rem 0 0.35rem">${escapeHtml(titulo)}</h3>`;
}

/** Identificação em linha corrida, sem tabela, para economizar espaço na folha. */
function identificacaoHtml(meta: RelatorioExportMeta): string {
  const campos = camposIdentificacao(meta)
    .map(
      (campo) =>
        `<span style="margin-right:1.25rem;white-space:nowrap"><strong>${escapeHtml(
          campo.rotulo,
        )}:</strong> ${escapeHtml(campo.valor)}</span>`,
    )
    .join('');

  return `<p style="font-size:10pt;line-height:1.6;margin:0 0 0.5rem">${campos}</p>`;
}

/** Resumo em pares seção/quantidade distribuídos em várias colunas. */
function tabelaResumoHtml(resumo: ResumoRelatorioLinha[], total: number): string {
  const corpo = linhasResumoGrid(resumo)
    .map((linha) => {
      const celulas = linha
        .map((celula, indice) => {
          const numerica = indice % 2 === 1;
          const estilo = numerica
            ? 'text-align:center;width:6%'
            : 'text-align:left;width:27.3%';
          return `<td style="${estilo}">${escapeHtml(String(celula))}</td>`;
        })
        .join('');
      return `<tr>${celulas}</tr>`;
    })
    .join('');

  const colunasTotal = PARES_RESUMO_POR_LINHA * 2 - 1;
  const rodape = `<tr><td style="text-align:left;font-weight:600" colspan="${colunasTotal}">Total de itens</td><td style="text-align:center;font-weight:600">${total}</td></tr>`;

  return `<table style="font-size:10pt"><tbody>${corpo}${rodape}</tbody></table>`;
}

/** Calendário mensal em grade (Semana x dias da semana), como na tela. */
function tabelaCalendarioHtml(calendario: CalendarioComparecimentoImpressao): string {
  const cabecalho = COLUNAS_CALENDARIO.map((coluna) => `<th>${escapeHtml(coluna)}</th>`).join('');
  const corpo = calendario.semanas
    .map((semana) => {
      const celulas = semana.dias
        .map((dia) => {
          if (!dia) return '<td></td>';
          const texto = textoDiaCalendario(dia);
          return `<td><strong>${dia.dia}</strong>${texto ? `<br />${escapeHtml(texto)}` : ''}</td>`;
        })
        .join('');
      return `<tr><td>${escapeHtml(semana.nome)}</td>${celulas}</tr>`;
    })
    .join('');

  return `<table><thead><tr>${cabecalho}</tr></thead><tbody>${corpo}</tbody></table>`;
}

/** Monta o HTML impresso: identificação, resumo, calendário e as seções que possuem itens. */
export function montarHtmlImpressaoRelatorio(
  meta: RelatorioExportMeta,
  calendario: CalendarioComparecimentoImpressao,
  resumo: ResumoRelatorioLinha[],
  secoesDetalhadas: SecaoRelatorioExport[],
  totalItens: number,
): string {
  const secoes = secoesDetalhadas
    .filter((secao) => secao.linhas.length > 0)
    .map((secao) => tituloSecaoHtml(secao.titulo) + tabelaHtml(secao.colunas, secao.linhas))
    .join('');

  const presencas = calendario.diasPresenca;

  const comparecimento = `<div style="page-break-before:always;page-break-inside:avoid;break-inside:avoid">${tituloSecaoHtml(
    `1. Comparecimento ao Fórum — ${meta.periodo} (${presencas} dia${presencas !== 1 ? 's' : ''} de presença)`,
  )}${tabelaCalendarioHtml(calendario)}</div>`;

  return [
    identificacaoHtml(meta),
    tituloSecaoHtml('Resumo por seção'),
    tabelaResumoHtml(resumo, totalItens),
    comparecimento,
    secoes,
    `<p style="margin-top:1rem;font-size:9pt;text-align:right">Emitido em ${escapeHtml(
      new Date().toLocaleString('pt-BR'),
    )}</p>`,
  ].join('');
}

/** Monta o HTML impresso: identificação, resumo, calendário e as seções que possuem itens. */
export function montarHtmlImpressaoRelatorioCivel(
  formData: RelatorioCivelFormData,
  meta: RelatorioExportMeta,
  calendario: CalendarioComparecimentoImpressao,
): string {
  return montarHtmlImpressaoRelatorio(
    meta,
    calendario,
    montarResumoRelatorioCivel(formData),
    montarSecoesRelatorioCivel(formData),
    totalItensRelatorioCivel(formData),
  );
}
